/**
 * Pavé numérique plein écran pour le code PIN parent (entrée du mode enfant,
 * sortie du mode enfant). Remplace un `<Input type="password">` : un champ
 * texte ouvre le clavier système iOS, qui recouvre la moitié de l'écran et
 * fait défiler la carte hors champ — un pavé de boutons n'ouvre jamais de
 * clavier.
 *
 * Validation automatique dès le dernier chiffre saisi : il n'y a que quatre
 * chiffres à taper, un bouton « Entrer » séparé n'ajoute qu'une étape.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Delete } from "lucide-react";
import { Logo } from "./Logo";
import { MutedText, ErrorBanner } from "./ui";
import { appendPinDigit, backspacePinDigit } from "../lib/pinPad";

const DIGIT_KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];

export function PinPad({
  title,
  subtitle,
  onSubmit,
  length = 4,
  errorMessage,
}: {
  title: string;
  subtitle: string;
  onSubmit: (pin: string) => Promise<boolean>;
  length?: number;
  errorMessage?: string;
}) {
  const { t } = useTranslation();
  const [pin, setPin] = useState("");
  const [error, setError] = useState(false);
  const [checking, setChecking] = useState(false);

  async function submit(candidate: string) {
    setChecking(true);
    try {
      const ok = await onSubmit(candidate);
      if (!ok) setError(true);
      // Le buffer est vidé dans les deux cas : sur échec pour laisser
      // ressaisir, sur succès pour préparer une éventuelle saisie suivante.
      // Quand l'appelant démonte le pavé aussitôt (cas le plus courant —
      // passage à l'écran suivant), ce vidage ne se voit jamais, le rendu
      // parent ayant déjà retiré le composant. Quand il reste monté (ex.
      // définition du code en deux temps dans les réglages), il est
      // indispensable pour présenter des cases vides à la seconde saisie.
      setPin("");
    } finally {
      setChecking(false);
    }
  }

  function handleDigit(digit: string) {
    if (checking) return;
    setError(false);
    const next = appendPinDigit(pin, digit, length);
    setPin(next);
    if (next.length === length) void submit(next);
  }

  function handleBackspace() {
    if (checking) return;
    setError(false);
    setPin((p) => backspacePinDigit(p));
  }

  // Le pavé à l'écran n'est pas la seule façon d'entrer un code : un clavier
  // physique (desktop, ou un lecteur d'écran qui pilote le focus au clavier)
  // doit marcher aussi. Écouteur sur `window` plutôt que sur le conteneur,
  // pour ne pas dépendre de quel élément a le focus.
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (checking) return;
      if (/^[0-9]$/.test(e.key)) {
        handleDigit(e.key);
      } else if (e.key === "Backspace") {
        handleBackspace();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin, checking, length]);

  return (
    <div className="w-full max-w-xs bg-surface border border-line rounded-card overprint ink-line p-6 flex flex-col items-center">
      <Logo size={48} className="self-start" />
      <h1 className="headline text-xl text-center mt-3">{title}</h1>
      <MutedText className="text-center mt-1">{subtitle}</MutedText>

      <div
        role="group"
        aria-label={t("kid.pinDigitsLabel", { count: pin.length, total: length })}
        className="flex gap-3 w-full mt-6"
      >
        {Array.from({ length }).map((_, i) => (
          <div
            key={i}
            className={`flex-1 h-14 rounded-card bg-night border flex items-center justify-center ${
              error ? "border-coral" : i === pin.length ? "border-sun" : "border-line"
            }`}
          >
            {i < pin.length && <span className="w-2.5 h-2.5 rounded-full bg-paper" />}
          </div>
        ))}
      </div>

      {/* Toujours monté (même vide), pour que `aria-live` annonce aussi
       * la disparition du message et pas seulement son apparition. */}
      <div aria-live="polite" className="w-full mt-4">
        {error && <ErrorBanner>{errorMessage ?? t("kid.pinError")}</ErrorBanner>}
      </div>

      <div className="grid grid-cols-3 gap-3 w-full mt-2">
        {DIGIT_KEYS.map((digit) => (
          <button
            key={digit}
            type="button"
            onClick={() => handleDigit(digit)}
            disabled={checking}
            className="tap-target h-14 rounded-card bg-night border border-line text-paper font-heading font-bold text-2xl overprint overprint-sm ink-line"
          >
            {digit}
          </button>
        ))}
        <div aria-hidden="true" />
        <button
          type="button"
          onClick={() => handleDigit("0")}
          disabled={checking}
          className="tap-target h-14 rounded-card bg-night border border-line text-paper font-heading font-bold text-2xl overprint overprint-sm ink-line"
        >
          0
        </button>
        <button
          type="button"
          onClick={handleBackspace}
          disabled={checking}
          aria-label={t("kid.pinDelete")}
          className="tap-target h-14 rounded-card bg-night border border-line text-paper font-heading font-bold text-2xl overprint overprint-sm ink-line flex items-center justify-center"
        >
          <Delete size={22} />
        </button>
      </div>
    </div>
  );
}
